export type ViewTransitionCallback = () => void | Promise<void>;

export const isViewTransitionSupported = (): boolean => {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
};

export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const shouldSkipTransition = (options?: { enabled?: boolean; respectReducedMotion?: boolean }): boolean => {
  const { enabled = true, respectReducedMotion = true } = options || {};
  return !enabled || !isViewTransitionSupported() || (respectReducedMotion && prefersReducedMotion());
};

export const startViewTransition = async (
  callback: ViewTransitionCallback,
  options?: {
    skipTransition?: boolean;
    waitForAnimation?: boolean;
    onTransitionReady?: () => void;
    onTransitionFinished?: () => void;
  },
): Promise<void> => {
  const { skipTransition = false, waitForAnimation = false, onTransitionReady, onTransitionFinished } = options || {};

  if (skipTransition || !isViewTransitionSupported()) {
    await callback();
    onTransitionFinished?.();
    return;
  }

  // Track to prevent double invocation on error
  let callbackInvoked = false;

  try {
    const transition = document.startViewTransition(async () => {
      callbackInvoked = true;
      await callback();
    });

    await transition.ready;
    onTransitionReady?.();

    if (waitForAnimation) {
      await transition.finished.finally(() => onTransitionFinished?.());
    } else {
      transition.finished.finally(() => onTransitionFinished?.());
    }
  } catch {
    // Only run callback if it wasn't already invoked by startViewTransition
    if (!callbackInvoked) {
      await callback();
    }
    onTransitionFinished?.();
  }
};

export const skipViewTransitionAnimation = (): void => {
  if (!isViewTransitionSupported()) return;

  document.documentElement.classList.add('skip-view-transition');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('skip-view-transition');
    });
  });
};

export const navigateWithTransition = async (
  navigationCallback: ViewTransitionCallback,
  options?: {
    enabled?: boolean;
    respectReducedMotion?: boolean;
    transitionType?: 'default' | 'slide' | 'fade' | 'cross-fade';
  },
): Promise<void> => {
  const { enabled = true, respectReducedMotion = true, transitionType = 'default' } = options || {};

  if (typeof document === 'undefined') {
    await navigationCallback();
    return;
  }

  const skip = shouldSkipTransition({ enabled, respectReducedMotion });

  if (!skip && transitionType !== 'default') {
    document.documentElement.dataset.viewTransition = transitionType;
  }

  await startViewTransition(navigationCallback, {
    skipTransition: skip,
    onTransitionFinished: () => {
      delete document.documentElement.dataset.viewTransition;
    },
  });
};

export const setViewTransitionName = (element: HTMLElement | null, name: string): void => {
  if (!element) return;
  element.style.viewTransitionName = name;
};

export const clearViewTransitionName = (element: HTMLElement | null): void => {
  if (!element) return;
  element.style.viewTransitionName = '';
};

export default {
  isViewTransitionSupported,
  prefersReducedMotion,
  shouldSkipTransition,
  startViewTransition,
  skipViewTransitionAnimation,
  navigateWithTransition,
  setViewTransitionName,
  clearViewTransitionName,
};
